import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { toast } from 'react-toastify'
import {
  IOperationsSlice,
  IOperationPlan,
  IOperationalAlert,
  IActiveTask,
  IHorizonResource,
  IResourceStatus,
  ITeamMember,
  ITrigger,
  IShiftProgram,
  IKPIData,
  IDepartmentBoardTask,
} from './types/operations.types'
import * as service from './operationsService'

const initialState: IOperationsSlice = {
  isLoading: false,
  kpis: null,
  plans: [],
  singlePlan: null,
  alerts: [],
  activeTasks: [],
  horizonResources: [],
  resourceStatus: null,
  teamMembers: [],
  triggers: [],
  shiftPrograms: [],
  singleShiftProgram: null,
  departmentTasks: [],
  totalCount: 0,
  totalPages: 0,
}

// ============================================================
// Async Thunks
// ============================================================

export const fetchOperationsKPIs = createAsyncThunk(
  'operations/fetchKPIs',
  async (_, thunkAPI) => {
    try {
      return await service.getOperationsKPIs()
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const fetchOperationPlans = createAsyncThunk(
  'operations/fetchPlans',
  async (params: any, thunkAPI) => {
    try {
      return await service.getOperationPlans(params)
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const fetchSinglePlan = createAsyncThunk(
  'operations/fetchSinglePlan',
  async (id: string | number, thunkAPI) => {
    try {
      return await service.getSingleOperationPlan(id)
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const fetchAlerts = createAsyncThunk(
  'operations/fetchAlerts',
  async (planId: string | number | undefined, thunkAPI) => {
    try {
      return await service.getOperationalAlerts(planId)
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const fetchActiveTasks = createAsyncThunk(
  'operations/fetchActiveTasks',
  async (planId: string | number | undefined, thunkAPI) => {
    try {
      return await service.getActiveTasks(planId)
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const fetchHorizonResources = createAsyncThunk(
  'operations/fetchHorizon',
  async (_, thunkAPI) => {
    try {
      return await service.getHorizonResources()
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const fetchResourceStatus = createAsyncThunk(
  'operations/fetchResourceStatus',
  async (_, thunkAPI) => {
    try {
      return await service.getResourceStatus()
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const fetchTeamMembers = createAsyncThunk(
  'operations/fetchTeamMembers',
  async (_, thunkAPI) => {
    try {
      return await service.getTeamMembers()
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const fetchTriggers = createAsyncThunk(
  'operations/fetchTriggers',
  async (_, thunkAPI) => {
    try {
      return await service.getTriggersList()
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const fetchShiftPrograms = createAsyncThunk(
  'operations/fetchShiftPrograms',
  async (_, thunkAPI) => {
    try {
      return await service.getShiftPrograms()
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const fetchDepartmentTasks = createAsyncThunk(
  'operations/fetchDepartmentTasks',
  async (departmentKey: string, thunkAPI) => {
    try {
      return await service.getDepartmentTasks(departmentKey)
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const createNewPlan = createAsyncThunk(
  'operations/createPlan',
  async (data: any, thunkAPI) => {
    try {
      const result = await service.createOperationPlan(data)
      toast.success('Operation plan created')
      return result
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const activatePlan = createAsyncThunk(
  'operations/activatePlan',
  async (id: string | number, thunkAPI) => {
    try {
      await service.activateOperationPlan(id)
      toast.success('Plan activated')
      return id
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const pausePlan = createAsyncThunk(
  'operations/pausePlan',
  async (id: string | number, thunkAPI) => {
    try {
      await service.pauseOperationPlan(id)
      toast.success('Plan paused')
      return id
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const toggleTriggerStatus = createAsyncThunk(
  'operations/toggleTrigger',
  async ({ id, enabled }: { id: string | number; enabled: boolean }, thunkAPI) => {
    try {
      await service.toggleTrigger(id, enabled)
      toast.success(enabled ? 'Trigger enabled' : 'Trigger disabled')
      return { id, enabled }
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const createNewShiftProgram = createAsyncThunk(
  'operations/createShiftProgram',
  async (data: any, thunkAPI) => {
    try {
      const result = await service.createShiftProgram(data)
      toast.success('Shift program created')
      return result
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

// ============================================================
// Slice
// ============================================================

const operationsSlice = createSlice({
  name: 'operations',
  initialState,
  reducers: {
    clearOperationsState: () => initialState,
    clearSinglePlan: (state) => {
      state.singlePlan = null
    },
  },
  extraReducers: (builder) => {
    // KPIs
    builder.addCase(fetchOperationsKPIs.fulfilled, (state, { payload }) => {
      state.kpis = payload as IKPIData
    })

    // Plans
    builder.addCase(fetchOperationPlans.pending, (state) => {
      state.isLoading = true
    })
    builder.addCase(fetchOperationPlans.fulfilled, (state, { payload }) => {
      state.isLoading = false
      state.plans = (payload as any).data as IOperationPlan[]
      state.totalCount = (payload as any).totalCount
      state.totalPages = (payload as any).totalPages
    })
    builder.addCase(fetchOperationPlans.rejected, (state) => {
      state.isLoading = false
    })

    // Single Plan
    builder.addCase(fetchSinglePlan.pending, (state) => {
      state.isLoading = true
    })
    builder.addCase(fetchSinglePlan.fulfilled, (state, { payload }) => {
      state.isLoading = false
      state.singlePlan = payload as IOperationPlan
    })
    builder.addCase(fetchSinglePlan.rejected, (state) => {
      state.isLoading = false
    })

    // Alerts
    builder.addCase(fetchAlerts.fulfilled, (state, { payload }) => {
      state.alerts = payload as IOperationalAlert[]
    })

    // Active Tasks
    builder.addCase(fetchActiveTasks.fulfilled, (state, { payload }) => {
      state.activeTasks = payload as IActiveTask[]
    })

    // Horizon
    builder.addCase(fetchHorizonResources.fulfilled, (state, { payload }) => {
      state.horizonResources = payload as IHorizonResource[]
    })

    // Resource Status
    builder.addCase(fetchResourceStatus.fulfilled, (state, { payload }) => {
      state.resourceStatus = payload as IResourceStatus
    })

    // Team
    builder.addCase(fetchTeamMembers.fulfilled, (state, { payload }) => {
      state.teamMembers = payload as ITeamMember[]
    })

    // Triggers
    builder.addCase(fetchTriggers.fulfilled, (state, { payload }) => {
      state.triggers = payload as ITrigger[]
    })
    builder.addCase(toggleTriggerStatus.fulfilled, (state, { payload }) => {
      const { id, enabled } = payload as { id: string | number; enabled: boolean }
      const trigger = state.triggers.find((t) => t.id === id)
      if (trigger) trigger.enabled = enabled
    })

    // Shift Programs
    builder.addCase(fetchShiftPrograms.fulfilled, (state, { payload }) => {
      state.shiftPrograms = (payload as any).data as IShiftProgram[]
    })

    // Department Tasks
    builder.addCase(fetchDepartmentTasks.pending, (state) => {
      state.isLoading = true
    })
    builder.addCase(fetchDepartmentTasks.fulfilled, (state, { payload }) => {
      state.isLoading = false
      state.departmentTasks = payload as IDepartmentBoardTask[]
    })
    builder.addCase(fetchDepartmentTasks.rejected, (state) => {
      state.isLoading = false
    })

    // Create Plan
    builder.addCase(createNewPlan.pending, (state) => {
      state.isLoading = true
    })
    builder.addCase(createNewPlan.fulfilled, (state) => {
      state.isLoading = false
    })
    builder.addCase(createNewPlan.rejected, (state) => {
      state.isLoading = false
    })
  },
})

export const { clearOperationsState, clearSinglePlan } = operationsSlice.actions
export default operationsSlice.reducer
